import { useCursor } from '@react-three/drei'
import { useMemo, useState } from 'react'
import * as THREE from 'three'

import { calculateRelativeAngle } from '../utils'

import type { PanoramaImage } from '@state/panorama.ts'

function useCalculatedMarker(current: PanoramaImage, target: PanoramaImage, domeRadius: number) {
  return useMemo(() => {
    const thetaCorrected = calculateRelativeAngle(current, target)

    // Angle below the horizon (how far "down" the marker appears)
    const elevationAngle = -Math.PI / 5

    // Position marker on the sphere surface at the correct angle
    const x = domeRadius * Math.sin(thetaCorrected) * Math.cos(elevationAngle)
    const y = domeRadius * Math.sin(elevationAngle)
    const z = domeRadius * Math.cos(thetaCorrected) * Math.cos(elevationAngle)

    // Build rotation: lay flat first, then rotate to point arrow away from user toward target
    const euler = new THREE.Euler(-Math.PI / 2, 0, thetaCorrected + Math.PI, 'XYZ')

    return {
      position: [x, y, z] as [number, number, number],
      rotation: euler,
    }
  }, [current, target, domeRadius])
}

interface MarkerProps {
  panoramaImage: PanoramaImage
  currentPanoramaImage: PanoramaImage
  onClick: () => void
  domeRadius: number
}

function GroundMarker({ onClick, panoramaImage, currentPanoramaImage, domeRadius }: MarkerProps) {
  const [hovered, setHover] = useState(false)
  const { position, rotation } = useCalculatedMarker(currentPanoramaImage, panoramaImage, domeRadius)

  useCursor(hovered)

  const arrowShape = useMemo(() => {
    const shape = new THREE.Shape()
    // Base geometry coordinates (optimized for scale=1)
    const top = 0.3
    const width = 0.4
    const arrowBottom = -0.1
    const thickness = 0.25

    shape.moveTo(0, top)
    shape.lineTo(width, arrowBottom)
    shape.lineTo(width, arrowBottom - thickness)
    shape.lineTo(0, top - thickness - 0.1)
    shape.lineTo(-width, arrowBottom - thickness)
    shape.lineTo(-width, arrowBottom)

    return shape
  }, [])

  const opacityMultiplier = hovered ? 0.6 : 1
  const radiusCircle = 0.9
  const thicknessRing = 0.1
  const radiusInnerRing = radiusCircle - thicknessRing
  const markerScale = domeRadius * 0.25

  return (
    <>
      <group
        scale={markerScale}
        position={[position[0], position[1], position[2]]}
        rotation={rotation}

      >
        {/* --- HITBOX --- */}
        {/* This invisible mesh catches all mouse events for the group. */}
        <mesh
          onClick={onClick}
          onPointerOver={() => setHover(true)}
          onPointerOut={() => setHover(false)}
        >
          <circleGeometry args={[radiusCircle, 32]} />
          <meshBasicMaterial
            transparent
            color="#f00"
            opacity={0} // Invisible but clickable
            depthWrite={false} // Prevents it from occluding other transparent objects
          />
        </mesh>

        {/* inner circle surface */}
        <mesh renderOrder={1}>
          <circleGeometry args={[radiusInnerRing, 64]} />
          <meshBasicMaterial
            color="#ccc"
            transparent
            opacity={opacityMultiplier * 0.5}
            side={THREE.DoubleSide}
            depthTest={false}
          />
        </mesh>

        {/* Ring */}
        <mesh>
          <ringGeometry args={[radiusInnerRing, radiusCircle, 64]} />
          <meshBasicMaterial
            color="#ccc"
            transparent
            opacity={opacityMultiplier * 0.8}
            side={THREE.DoubleSide}
            depthTest={false}
          />
        </mesh>

        {/* Arrow */}
        <mesh position={[0, 0.01, 0]} renderOrder={2}>
          <shapeGeometry args={[arrowShape]} />
          <meshBasicMaterial
            color="#000"
            side={THREE.DoubleSide}
            depthTest={false}
            opacity={opacityMultiplier * 0.8}
          />
        </mesh>
      </group>
    </>
  )
}

export default GroundMarker

